import React, { useMemo } from 'react'
import {
  DataGrid,
  EmptyState,
  Icon,
  Spinner,
  theme,
  IconDictionary,
  TabSegmented,
  TabSegmentedGroup,
} from '@ketch-com/deck'
import { Box, Stack, Typography } from '@mui/material'
import { useTableFilters } from 'pages/dataSentry/hooks/useTableFilters'
import { SearchTextInput } from 'components/searchTextInput/SearchTextInput'
import { liveRightsDataGridColumns, RightRow, transformApiDataToRows } from './LiveRequestsTableUtil'

interface LiveRequestsTableProps {
  reportType: 'right' | 'canonical-right'
  setReportType: React.Dispatch<React.SetStateAction<'right' | 'canonical-right'>>
  rightNameData: RightRow[]
  rightTypeData: RightRow[]
  isLoading: boolean
}

export const LiveRequestsTable = ({
  reportType,
  setReportType,
  rightNameData,
  rightTypeData,
  isLoading,
}: LiveRequestsTableProps) => {
  const { searchQuery, setSearchQuery, paginationModel, setPaginationModel } = useTableFilters()
  const allRows = reportType === 'right' ? transformApiDataToRows(rightNameData) : transformApiDataToRows(rightTypeData)

  const columns = useMemo(() => liveRightsDataGridColumns, [])
  const filteredRows = useMemo(() => {
    if (!searchQuery) return allRows
    const q = searchQuery.toLowerCase()
    return allRows.filter(row => row.right.toLowerCase().includes(q) || row.code.toLowerCase().includes(q))
  }, [allRows, searchQuery])

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      gap={2}
      sx={{ backgroundColor: theme.palette.Common.Container }}
      borderRadius="11px"
      padding="16px 20px"
    >
      {/* Header & Controls */}
      <Stack direction="row" justifyContent="space-between">
        <Box>
          <Typography variant="h4">Requests Per Right</Typography>
          <Typography variant="body" color={theme.palette.Text.Secondary}>
            Breakdown of Unfulfilled Requests by Status
          </Typography>
        </Box>
        {allRows.length > 0 && (
          <Stack direction="row" spacing={2} alignItems="center">
            <SearchTextInput
              onChange={setSearchQuery}
              value={searchQuery}
              showClearButton
              minWidth="220px"
              sx={{
                width: '220px',
                '& .MuiInputBase-input': { maxWidth: '156px', minWidth: '156px' },
              }}
            />
            <TabSegmentedGroup size="small" value={reportType} exclusive onChange={(_, value) => setReportType(value)}>
              <TabSegmented value="right">
                <Box display="flex" gap={0.5} alignItems="center">
                  <Typography variant="label">Right Name</Typography>
                </Box>
              </TabSegmented>
              <TabSegmented value="canonical-right">
                <Box display="flex" gap={0.5} alignItems="center">
                  <Typography variant="label">Right Type</Typography>
                </Box>
              </TabSegmented>
            </TabSegmentedGroup>
          </Stack>
        )}
      </Stack>
      {/* Loading State */}
      {isLoading && filteredRows.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '350px' }}>
          <Spinner size="32px" thickness={2} />
        </Box>
      )}
      {/* Empty State */}
      {!isLoading && filteredRows.length === 0 && (
        <Box display="flex" justifyContent="center" alignItems="center" height="350px" p={2}>
          <EmptyState
            size="small"
            colorTheme={theme.palette.Text.Secondary}
            sx={{ backgroundColor: theme.palette.Black.o8, borderRadius: 4 }}
            customIcon={
              <Icon name={IconDictionary.OTableView} width="48px" height="48px" iconColor={theme.palette.Black.o48} />
            }
            title="No Data"
            subTitle="Adjust your search criteria or filters and try again."
          />
        </Box>
      )}
      {/* Data Grid */}
      {filteredRows.length > 0 && (
        <DataGrid
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox):hover': {
              backgroundImage: 'unset',
              cursor: 'auto',
            },
            ...(isLoading && {
              '& .MuiDataGrid-virtualScroller': {
                height: '300px',
              },
            }),
          }}
          autosizeOnMount
          autosizeOptions={{
            expand: true,
            includeHeaders: true,
            includeOutliers: false,
          }}
          getRowHeight={() => 'auto'}
          columns={columns}
          getRowId={row => row.id}
          rows={filteredRows}
          checkboxSelection={false}
          hideFooter={reportType === 'canonical-right'}
          disableRowSelectionOnClick
          disableColumnMenu
          disableColumnPinning
          disableColumnReorder
          disableColumnResize
          disableChildrenSorting={false}
          disableRowHoverStates
          loading={isLoading}
          pagination
          paginationMode="client"
          rowCount={filteredRows.length}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 20]}
          noRowsOverlayProps={{ buttonTitle: '' }}
        />
      )}
    </Box>
  )
}
